import React from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';

/**
 * Variance Analysis Cards
 * Shows Plan vs Actual comparisons for volume, price, and revenue
 * Only displays for years with actual data
 */
const VarianceAnalysis = ({ forecasts }) => {
  // Filter forecasts that have actual data
  const forecastsWithActuals = forecasts.filter(
    f => f.has_actuals === true || (
      f.has_actuals === undefined &&
      (f.volume_actual !== null && f.volume_actual !== undefined)
    )
  );

  if (forecastsWithActuals.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Variance Analysis</h3>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <AlertCircle className="h-5 w-5" />
          <span>No actual data available for variance analysis. Enter actuals to see comparisons.</span>
        </div>
      </div>
    );
  }

  // Calculate aggregated variances
  const totals = forecastsWithActuals.reduce(
    (acc, f) => {
      acc.volumePlan += f.volume_plan || 0;
      acc.volumeActual += f.volume_actual || 0;
      acc.revenuePlan += (f.volume_plan * (f.price_plan || 0));
      acc.revenueActual += (f.volume_actual * (f.price_actual || f.price_plan || 0));
      acc.count += 1;
      
      // Average price calculation
      acc.pricePlanSum += f.price_plan || 0;
      acc.priceActualSum += f.price_actual || f.price_plan || 0;
      
      return acc;
    },
    {
      volumePlan: 0,
      volumeActual: 0,
      revenuePlan: 0,
      revenueActual: 0,
      pricePlanSum: 0,
      priceActualSum: 0,
      count: 0
    }
  );

  // Calculate variances
  const volumeVariance = totals.volumeActual - totals.volumePlan;
  const volumeVariancePercent = totals.volumePlan > 0
    ? (volumeVariance / totals.volumePlan) * 100
    : 0;

  const avgPricePlan = totals.count > 0 ? totals.pricePlanSum / totals.count : 0;
  const avgPriceActual = totals.count > 0 ? totals.priceActualSum / totals.count : 0;
  const priceVariance = avgPriceActual - avgPricePlan;
  const priceVariancePercent = avgPricePlan > 0
    ? (priceVariance / avgPricePlan) * 100
    : 0;

  const revenueVariance = totals.revenueActual - totals.revenuePlan;
  const revenueVariancePercent = totals.revenuePlan > 0
    ? (revenueVariance / totals.revenuePlan) * 100
    : 0;

  // Helper function to format numbers
  const formatNumber = (num) => {
    return Math.abs(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const periodLabel = forecastsWithActuals.some(f => f.month !== null && f.month !== undefined)
    ? 'month'
    : 'year';

  const formatCurrency = (num) => {
    return `$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (num) => {
    return `${Math.abs(num).toFixed(1)}%`;
  };

  // Helper function to get trend icon and color
  const getTrendInfo = (variance, isRevenueOrVolume = true) => {
    const isPositive = variance > 0;
    const isNegative = variance < 0;
    
    // For revenue and volume, positive is good. For price, it depends on context.
    const colorClass = isRevenueOrVolume
      ? isPositive ? 'text-green-600 bg-green-50 border-green-200'
        : isNegative ? 'text-red-600 bg-red-50 border-red-200'
        : 'text-gray-600 bg-gray-50 border-gray-200'
      : isPositive ? 'text-yellow-600 bg-yellow-50 border-yellow-200'
        : isNegative ? 'text-blue-600 bg-blue-50 border-blue-200'
        : 'text-gray-600 bg-gray-50 border-gray-200';
    
    const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
    const sign = isPositive ? '+' : isNegative ? '-' : '';
    
    return { Icon, colorClass, sign };
  };

  // Render variance card
  const VarianceCard = ({ title, planValue, actualValue, variance, variancePercent, isRevenueOrVolume, isCurrency }) => {
    const { Icon, colorClass, sign } = getTrendInfo(variance, isRevenueOrVolume);
    
    return (
      <div className={`p-6 rounded-lg border ${colorClass}`}>
        <div className="flex items-start justify-between mb-4">
          <h4 className="text-sm font-medium uppercase tracking-wider">{title}</h4>
          <Icon className="h-5 w-5" />
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Plan:</span>
            <span className="font-medium">
              {isCurrency ? formatCurrency(planValue) : formatNumber(planValue)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Actual:</span>
            <span className="font-semibold">
              {isCurrency ? formatCurrency(actualValue) : formatNumber(actualValue)}
            </span>
          </div>
          <div className="pt-3 border-t border-current border-opacity-20">
            <div className="flex justify-between items-baseline">
              <span className="text-sm font-medium">Variance:</span>
              <div className="text-right">
                <div className="text-lg font-bold">
                  {sign}{isCurrency ? formatCurrency(variance) : formatNumber(variance)}
                </div>
                <div className="text-sm font-semibold">
                  {sign}{formatPercent(variancePercent)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900">Variance Analysis</h3>
        <p className="text-sm text-gray-500 mt-1">
          Plan vs Actual comparison for {totals.count} {periodLabel}{totals.count === 1 ? '' : 's'} with actual data
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <VarianceCard
          title="Volume Variance"
          planValue={totals.volumePlan}
          actualValue={totals.volumeActual}
          variance={volumeVariance}
          variancePercent={volumeVariancePercent}
          isRevenueOrVolume={true}
          isCurrency={false}
        />
        
        <VarianceCard
          title="Price Variance"
          planValue={avgPricePlan}
          actualValue={avgPriceActual}
          variance={priceVariance}
          variancePercent={priceVariancePercent}
          isRevenueOrVolume={false}
          isCurrency={true}
        />
        
        <VarianceCard
          title="Revenue Impact"
          planValue={totals.revenuePlan}
          actualValue={totals.revenueActual}
          variance={revenueVariance}
          variancePercent={revenueVariancePercent}
          isRevenueOrVolume={true}
          isCurrency={true}
        />
      </div>
    </div>
  );
};

export default VarianceAnalysis;
